<template lang="pug">
.subscription-box-order-fraud-review(v-if="order", v-loading="loading", :class="{ embedded }")

  el-row(v-if="showActions")
    el-col(:span="12")
      h3 {{ title }}

    el-col(:span="12", style="text-align: right")
      el-dropdown(@command="executeAction", trigger="click")
        el-button(type="primary", plain, size="small")
          | Revisar
          i.el-icon-arrow-down.el-icon--right

        el-dropdown-menu(slot="dropdown")
          el-dropdown-item(command="approve", :disabled="!showApprove")
            i.el-icon-check
            | Aprovado

          el-dropdown-item(command="decline", :disabled="!showReject")
            i.el-icon-warning
            | Negado

          el-dropdown-item(command="reject", :disabled="!showReject")
            i.el-icon-error
            | Fraude

  el-row(v-else)
    el-col(:span="24")
      h3 {{ title }}

  el-row(v-if="!embedded")
    el-col(:span="24", style="text-align: right;")
      a(:href="kondutoUrl", target="_blank")
        el-button(type="text", size="small") Ver na Konduto

  el-tabs(tab-position="left")

    el-tab-pane(label="Dados do pedido", lazy)
      .tab-content
        SubscriptionBoxOrderDetail(:id="orderId", :show-header="false", tab-position="top")

    el-tab-pane(label="Dados do cliente", v-if="customerId", lazy)
      .tab-content
        CustomerDetail(:id="customerId", :show-header="false", tab-position="top")

    el-tab-pane(label="Dados da assinatura", v-if="subscriptionId", lazy)
      .tab-content
        SubscriptionDetail(:id="subscriptionId", :show-header="false", tab-position="top")

</template>
<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue2-helpers/vue-router'
import CustomerDetail from '@/views/customer/Detail.vue'
import SubscriptionDetail from '@/views/billing/subscription/Detail.vue'
import SubscriptionBoxOrderDetail from '../SubscriptionBoxOrderDetail.vue'
import SubscriptionBoxOrderService from '@/services/fulfillment/SubscriptionBoxOrderService'
import InvoiceService from '@/services/billing/InvoiceService'
import CustomerService from '@/services/CustomerService'
import { useMessage } from '@/utils/Message'
import { useConfirm } from '@/utils/MessageBox'

const router = useRouter()

const message = useMessage()
const confirm = useConfirm()

const props = defineProps({
  id: {
    required: true,
  },
  embedded: {
    type: Boolean,
    default: true,
  },
  showActions: {
    type: Boolean,
    default: true,
  },
})

const order = ref(null)
const orderId = computed(() => props.id)

const loading = ref(true)
const customerId = ref()
const customer = ref()
const subscriptionId = ref()

const title = computed(() => {
  if (customer.value) {
    return `${customer.value.firstName} ${customer.value.lastName} - Pedido #${orderId.value}`
  }
  return `Revisão do pedido #${orderId.value}`
})

const kondutoUrl = computed(() => {
  return `https://app.konduto.com/orders/48416/${orderId.value}`
})

const showApprove = computed(() => {
  return order.value && (order.value.status == 'SUSPECTED_FRAUD' || order.value.status == 'FRAUD' || order.value.status == 'DENIED')
})

const showReject = computed(() => {
  return order.value && order.value.status != 'FRAUD' && order.value.status != 'CANCELED' && order.value.status != 'DENIED'
})

const executeAction = (action) => {
  switch (action) {
    case 'approve':
      approve()
      break
    case 'decline':
      decline()
      break
    case 'reject':
      reject()
      break
    default:
      message.error('Ação inválida')
  }
}

const approve = () => {
  confirm('Deseja aprovar este pedido? Sua produção será liberada', 'Aprovar pedido', {
    confirmButtonText: 'Aprovar',
    cancelButtonText: 'Cancelar',
    type: 'success',
  }).then(async () => {

    try {
      loading.value = true

      await SubscriptionBoxOrderService.approveFraudReview(orderId.value)
      message.success('Pedido aprovado com sucesso')

      reload()
    } catch(e) {
      message.error('Falha ao aprovar pedido')
    } finally {
      loading.value = false
    }
  })
}

const decline = () => {
  confirm('Deseja negar este pedido? Seu pagamento será estornado e sua assinatura cancelada', 'Negar pedido', {
    confirmButtonText: 'Negar',
    cancelButtonText: 'Cancelar',
    type: 'warning',
  }).then(async () => {
    try {
      loading.value = true

      await SubscriptionBoxOrderService.declineFraudReview(orderId.value)
      message.success('Pedido negado com sucesso')

      reload()
    } catch(e) {
      message.error('Falha ao negar pedido')
    } finally {
      loading.value = false
    }
  })
}

const reject = () => {
  confirm('Deseja reprovar este pedido como fraude? Seu pagamento será estornado e sua assinatura cancelada', 'Reprovar pedido como fraude', {
    confirmButtonText: 'Reprovar',
    cancelButtonText: 'Cancelar',
    type: 'danger',
  }).then(async () => {
    try {
      loading.value = true

      await SubscriptionBoxOrderService.rejectFraudReview(orderId.value)
      message.success('Pedido reprovado com sucesso')

      reload()
    } catch(e) {
      message.error('Falha ao reprovar pedido')
    } finally {
      loading.value = false
    }
  })
}

const back = () => {
  router.push({ name: 'subscription-box-order-fraud-review-list' })
}

const reload = async () => {
  try {
    order.value = await SubscriptionBoxOrderService.getSubscriptionBoxOrderById(props.id)
    const invoice = order.value.invoiceId ? await InvoiceService.getInvoiceById(order.value.invoiceId) : null

    customerId.value = order.value.customerId
    subscriptionId.value = invoice?.subscriptionId

    if (customerId.value) {
      customer.value = await CustomerService.getCustomerById(customerId.value)
    }
  } catch(e) {
    message.error('Falha ao carregar dados do pedido')

    router.back()
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await reload()
})
</script>
<style lang="sass" scoped>
.subscription-box-order-fraud-review

  &.embedded
    position: relative
    height: calc(100vh - 48px)
    padding: 32px 16px 16px 16px

    .tab-content
      max-height: 606px
      overflow: auto

  .tab-content
    padding: 0 16px
</style>
<style lang="sass">
.subscription-box-order-fraud-review.embedded

  .profile-questionnaire
    position: relative
    max-height: 552px
    overflow: auto
</style>
